
import { PusatResolusiV2Controller } from '@/app/ui/controllers/PusatResolusiV2Controller';
import { FinishIssuePusatResolusiV2Request, GetConversationRequest } from '@/data/payload/api/PusatResolusiV2ApiRequest';
import { Conversation, HistoryChatResponse } from '@/domain/entities/PusatResolusiV2';
import { Options, prop, Vue } from 'vue-class-component';
import { dateToDateString, formatDate, parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import groupBy from "lodash/groupBy";
import ImagePreview from "@/app/ui/views/pusat-resolusi-v2/components/image-preview.vue";
import downloadFile from "@/app/infrastructures/misc/common-library/DownloadFile";
import fileNameFromUrl from "@/app/infrastructures/misc/common-library/FileNameFromUrl";
import fileExtensionFromUrl from "@/app/infrastructures/misc/common-library/FileExtensionFromUrl";
import Checkbox from 'primevue/checkbox';
import { CLIENT_ERROR, INTERNET_ERROR, INTERNAL_SERVER_ERROR } from '@/app/infrastructures/misc/Utils';
import { PusatResolusiV2Mapper } from '@/data/persistences/mappers/PusatResolusiV2Mapper';
import { lastTicketStatus, mappingListPriority } from '../../utils/status-mapping';
import { MainAppController } from '@/app/ui/controllers/MainAppController';
import ModalFinishIssue from './modal-finish-issue.vue';
import ModalSuccessFinish from "./modal-success-finish.vue";
import { AccountController } from '@/app/ui/controllers/AccountController';

class Props {
  detailId = prop<number>({
    required: true
  });
  websocket = prop<WebSocket>({
    required: true
  });
  caseNumber = prop<string>({
    required: true
  });
}

@Options({
  components: {
    ImagePreview,
    Checkbox,
    ModalFinishIssue,
    ModalSuccessFinish,
  },
  emits: [],
  watch: {
    websocket: {
      handler(ws: WebSocket) {
        ws.addEventListener("message", async event => {
          const mapper = new PusatResolusiV2Mapper();
          const conversation = mapper.convertHistoryChatResponseFromWebsocket(event.data)[0];

          if (this.isMounted) {
            await this.$nextTick();
            const id = this.historyChatData.data.conversations[this.historyChatData.data.conversations.length - 1]?.id;
            const element = document.getElementById("conversation-element-"+id);
            if (element) {
              setTimeout(() => {
                element.scrollIntoView({
                  behavior: "smooth",
                  block: "end",
                  inline: "end"
                });
              }, 100)
            }
            this.isMounted = false;
          }
          if (!conversation.type) return;
          this.websocketAction(conversation);
        })
      }
    }
  }
})

export default class HistoryChat extends Vue.with(Props) {
  CLIENT_ERROR = CLIENT_ERROR;
  INTERNAL_SERVER_ERROR = INTERNAL_SERVER_ERROR;
  INTERNET_ERROR = INTERNET_ERROR;
  
  showImagePreview = false;
  selectedImage = null as number | null;
  isLoading = false;
  errorState = "";
  isHighlight = false;
  mappingListPriority = mappingListPriority;
  isMounted = true;
  showImageConversationId = 0;
  historyChatData = new HistoryChatResponse()
  historyChatRealtimeData: Conversation[] = [];
  previousScrollHeightMinusScrollTop = 0;
  conversationContainerBottomOffset = 0;
  isAllConversationLoaded = false;
  
  // unread realtime logic: START
  isUnreadRealtime = false;
  realtimeLastReadId = 0;
  realtimeNewMessageId = 0;
  realtimeReadObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.realtimeReadObserver.unobserve(entry.target);
        const idSplit = entry.target.id.split("-");
        const convId = Number(idSplit[idSplit.length - 1]);
        if (convId > this.realtimeLastReadId) {
          this.realtimeLastReadId = convId;
        }
      }
    });
  });

  get allConversation() {
    return this.historyChatData.data.conversations.concat(this.historyChatRealtimeData)
  }

  get realtimeUnreadCount() {
    return this.allConversation
      .filter(item => item.id > this.realtimeLastReadId 
        && item.createdName !== AccountController.accountData.username
      )
      .length
  }
  // unread realtime logic: END

  async mounted() {
    await this.getHistoryChat();
    await this.$nextTick();
    this.handleInfiniteScroll();
  }

  unmounted() {
    window.dispatchEvent(new Event("unmount-history-chat"))
    this.lastReadObserver.disconnect();
    this.realtimeReadObserver.disconnect();
  }

  initHistoryChat(response: HistoryChatResponse) {
    if (!this.historyChatData.data.conversations.length) {
      this.historyChatData.data.unreadCount = response.data.unreadCount;
      this.historyChatData.data.lastReadId = response.data.lastReadId;
      this.historyChatData.data.lastReadAt = response.data.lastReadAt;
      this.historyChatData.data.firstUnreadMessageAt = response.data.firstUnreadMessageAt;
      this.isHighlight = response.data.unreadCount > 0;
    }
    // menyimpan last read id pertama kali
    const lastMessage = response.data.conversations.length - 1
    const lastMessageId = response.data.conversations[lastMessage]?.id || 0
    if (lastMessageId > this.realtimeLastReadId) {
      this.realtimeLastReadId = lastMessageId;
    }
  }

  async getHistoryChat() {
    if (this.isLoading) return;
    try {
      this.errorState = "";
      this.isLoading = true;
      const element = document.getElementById("conversation-container");
      if (element) {
        this.previousScrollHeightMinusScrollTop = element?.scrollHeight - element?.scrollTop
      }
      const response = await PusatResolusiV2Controller.getConversation({
        id: Number(this.detailId),
        params: new GetConversationRequest({
          limit: this.historyChatData.limit,
          beforeId: this.historyChatData.beforeId
        })
      });

      this.checkLoadedChat(response.data.conversations);

      this.historyChatData.beforeId = response.beforeId;
      this.historyChatData.limit = response.limit;
      this.historyChatData.totalRecords = response.totalRecords;
      const newConv = response.data.conversations.concat(this.historyChatData.data.conversations);
      this.$emit("update-conversation", newConv)

      this.initHistoryChat(response);

      this.historyChatData.data.conversations = newConv;
      if (element) {
        await this.$nextTick();;
        element.scrollTop = element.scrollHeight - this.previousScrollHeightMinusScrollTop;
      }

      this.observeUnreadMessage();
    } catch (error) {
      this._handleErrorGetTicketDetail(error);
    } finally {
      this.isLoading = false;
    }
  }

  get groupedConversation() {
    const conversations = this.allConversation;
    const groupedDataObject = groupBy(conversations, "groupDate");
    const data: any = [];
    const isGroupingUnread = dateToDateString(this.historyChatData.data.lastReadAt) !== dateToDateString(this.historyChatData.data.firstUnreadMessageAt) && this.historyChatData.data.firstUnreadMessageAt ? true : false;


    Object.entries(groupedDataObject).forEach(([date, items]) => {
      data.push({
        date: this.getDateLabel(date),
        items: items,
        lastReadId: this.historyChatData.data.lastReadId,
        lastReadAt: this.historyChatData.data.lastReadAt,
        firstUnreadMessageAt: this.historyChatData.data.firstUnreadMessageAt,
        unreadCount: this.historyChatData.data.unreadCount,
        isGroupingUnread: isGroupingUnread,
        groupDateUnread: this.historyChatData.data.firstUnreadMessageAt ? this.getDateLabel(this.historyChatData.data.firstUnreadMessageAt) === this.getDateLabel(date) : false
      });
    });

    return data;
  }

  websocketAction(conversation: Conversation) {
    const conversationContainer = document.getElementById("conversation-container");
    if (!conversationContainer) return;
    this.conversationContainerBottomOffset = conversationContainer.scrollHeight - conversationContainer.scrollTop - conversationContainer.clientHeight;

    const obj = {
      CS_AGENT_CHANGE: () => {
        this.pushConversation(conversation);
      },
      PRIORITY_CHANGE: () => {
        this.pushConversation(conversation);
      },
      STATUS_CHANGE: () => {
        this.pushConversation(conversation);

        if (lastTicketStatus.includes(conversation.value.status)) {
          this.historyChatData.data.conversations = this.historyChatData.data.conversations.map((conv) => {
            if (conv.type === "ESCALATION_NOTE") return { ...conv, value: { ...conv.value, isSolved: true } }
            return conv;
          })

          this.historyChatRealtimeData = this.historyChatRealtimeData.map((conv) => {
            if (conv.type === "ESCALATION_NOTE") return { ...conv, value: { ...conv.value, isSolved: true } }
            return conv;
          })
        }
      },
      CATEGORY_CHANGE: () => {
        this.pushConversation(conversation);
      },
      ESCALATION_NOTE: () => {
        this.pushConversation(conversation);
      },
      ESCALATE_DONE: () => {
        this.pushConversation(conversation);
        this.historyChatRealtimeData = this.historyChatRealtimeData.map(conv => {
          if (conv.id === conversation.value.escalateNoteConversationId) return { ...conv, value: { ...conv.value, isSolved: true } }
          return conv
        })
        this.historyChatData.data.conversations = this.historyChatData.data.conversations.map(conv => {
          if (conv.id === conversation.value.escalateNoteConversationId) return { ...conv, value: { ...conv.value, isSolved: true } }
          return conv
        })
      },
      DESCRIPTION: () => {
        this.pushConversation(conversation);
      },
      CHAT: () => {
        this.pushConversation(conversation);
      },
      ESCALATE_TO: () => {
        this.pushConversation(conversation);
      },
    } as any

    if (!obj[conversation.type]) return;
    obj[conversation.type]();

  }

  async pushConversation(conversation: Conversation) {
    this.historyChatRealtimeData.push(conversation);
    let offset = 40;
    const conversationContainer = document.getElementById("conversation-container");
    if (!conversationContainer) return;
    if (conversation.createdName === AccountController.accountData.username) {
      offset = 1000;
    } 
    const shouldScrollBottom = Math.abs(this.conversationContainerBottomOffset) <= offset;
    if (shouldScrollBottom) {
      await this.$nextTick();
      conversationContainer?.scrollTo({
        behavior: "smooth",
        top: conversationContainer.scrollHeight,
      })
    }
    // unread realtime logic: START
    await this.$nextTick();
    const convElement = document.getElementById(`conversation-element-${conversation.id}`)
    if (convElement) {
      this.realtimeReadObserver.observe(convElement)
    }
    this.updateRealtimeUnread(shouldScrollBottom)
    // unread realtime logic: END
  }

  async updateRealtimeUnread(isOnBottom: boolean) {
    await this.$nextTick();
    if (this.isHighlight && this.isUnreadRealtime) {
      this.historyChatData.data.unreadCount = this.realtimeUnreadCount;
      return
    }
    if (this.isHighlight || !this.realtimeUnreadCount) return
    if (!isOnBottom) {
      this.isHighlight = true;
      this.historyChatData.data.unreadCount = this.realtimeUnreadCount;
    }
    this.isUnreadRealtime = true;
    const lastReadIndex = this.allConversation.findIndex((item) => item.id === this.realtimeLastReadId)
    const firstNewMessage = this.allConversation[lastReadIndex + 1]
    this.historyChatData.data.firstUnreadMessageAt = firstNewMessage.createdAt;
    this.historyChatData.data.lastReadAt = this.allConversation[lastReadIndex].createdAt;
    this.lastReadObserver.disconnect();
    this.historyChatData.data.lastReadId = this.realtimeLastReadId;
    this.realtimeNewMessageId = firstNewMessage.id;
    const element = this.getConversationElement(this.realtimeLastReadId);
    if (element) {
      this.lastReadObserver.observe(element);
    }
  }
  
  public getDateLabel(inputDate: string) {
    const input = new Date(inputDate);
    const now = new Date();

    const inputDateOnly = new Date(input.getFullYear(), input.getMonth(), input.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (inputDateOnly.getTime() === today.getTime()) {
      return 'Hari ini';
    }

    if (inputDateOnly.getTime() === yesterday.getTime()) {
      return 'Kemarin';
    }

    return dateToDateString(inputDate, true);
  }

  isCardView(conversation: Conversation) {
    const type = conversation.type;
    return type === "DESCRIPTION" || type === "ESCALATION_NOTE" || type === "CHAT"
  }

  mapStatusTicket(conversation: Conversation) {
    const status = conversation.value.status;
    const statusLabel: any = () => {
      const obj = {
        NEW: "baru",
        WORKING: "dalam proses",
        ESCALATED: "dalam eskalasi",
        "ON HOLD": "ditunda sementara",
        SOLVED: "selesai",
        CLOSED: "ditutup"
      } as any
      return obj[status] ?? status;
    }
    const style: any = () => {
      const obj = {
        NEW: "text-blue-lp-500",
        WORKING: "text-yellow-lp-100",
        ESCALATED: "text-purple-lp-700",
        "ON HOLD": "text-gray-lp-300",
        SOLVED: "text-green-lp-200",
        CLOSED: "text-chocolate-lp-200"
      } as any
      return obj[status] ?? "";
    }
    const madeBy = status === "new" ? "dibuat oleh" : "oleh"
    return {
      label: statusLabel(),
      style: style(),
      madeBy: madeBy
    }
  }

  userInformationLabel(conversation: Conversation) {
    const createdName = conversation.createdName ? `${conversation.createdByPartnerCode ? ' - ' : ''}${conversation.createdName}` : ""
    return `${this.getPartnerName(conversation)}${createdName}`;
  }

  getPartnerName(conversation: Conversation) {
    return conversation.createdByPartnerCode ? `${conversation.createdByPartnerCode} - ${conversation.createdByPartnerName}` : "";
  }

  getTime(date: any) {
    return formatDate(date).split(", ")[1];
  }
    
  openImagePreview(index: number, id: number): void {
    this.selectedImage = index;
    this.showImagePreview = true;
    this.showImageConversationId = id;
  }

  closeImagePreview(): void {
    this.showImagePreview = false;
    this.selectedImage = null;
    this.showImageConversationId = 0;
  }

  isImageFile(ext?: string): boolean {
    return ["png", "jpg", "jpeg", "gif", "webp"].includes(ext?.toLowerCase() ?? "");
  }

  isPdfFile(ext?: string): boolean {
    return ext?.toLowerCase() === "pdf";
  }

  getFileColor(ext?: string): string {
    if (!ext) return "#EDEDED";

    const lowerExt = ext.toLowerCase();
    switch (lowerExt) {
      case "doc":
      case "docx":
        return "#155EEF";
      case "pdf":
        return "#AD0C00";
      case "xlsx":
        return "#016F3E";
      case "xls":
        return "#079455";
      case "csv":
        return "#EDEDED";
      default:
        return "#EDEDED";
    }
  }

  getFileExtension(filename: string): string {
    return filename.split('.').pop() || '';
  }

  downloadFile(url: string): void {
    downloadFile(url, fileNameFromUrl(url), fileExtensionFromUrl(url));
  }

  mapChatLabel(conversation: Conversation) {
    return conversation.type === "ESCALATION_NOTE" ? conversation.createdName : this.userInformationLabel(conversation);
  }

  _handleErrorGetTicketDetail(error: any) {
    if (!error.response) {
      this.errorState = INTERNET_ERROR;
      return;
    }
    if (error.response.status > 500) {
      this.errorState = CLIENT_ERROR;
      return;
    }

    this.errorState = INTERNAL_SERVER_ERROR;
  }

  async handleInfiniteScroll() {
    await this.$nextTick()
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (this.isAllConversationLoaded) return;
          this.getHistoryChat();
        }
      });
    }, {
      });

    const element = document.getElementById("infinite-scroll-conversation-chat");
    if (element) {
      observer.observe(element);
      window.addEventListener("unmount-history-chat", () => {
        observer.disconnect();
      })
    }

  }

  mapConversationImages(conversation: Conversation) {
    return conversation.value.attachments
      .filter((item) => this.isImageFile(item.ext))
      .map((item) => item.url)
  }

  mapConversationTitle(conversation: Conversation) {
    return conversation.value.attachments
      .filter((item) => this.isImageFile(item.ext))
      .map((item) => item.name)
  }

  getConversationElement(id: number) {
    return document.getElementById(`conversation-element-${id}`);
  }

  async readUnreadMessage() {
    let element = this.getConversationElement(this.historyChatData.data.lastReadId);
    if (this.isUnreadRealtime && this.realtimeNewMessageId) {
      element = this.getConversationElement(this.realtimeNewMessageId)
    }
    const observer = this.lastReadObserver;

    if (element) {
      element?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
      })
      observer.observe(element)
      MainAppController.closeLoading();
      window.addEventListener("unmount-history-chat", () => {
        observer.disconnect();
      })
    } else {
      MainAppController.showLoading();
      await this.getHistoryChat();
      await this.$nextTick();
      this.readUnreadMessage()
    }

  }
  // modal finish
  modalFinish = false;
  modalSuccessFinish = false;
  onShowModalFinish() {
    this.modalFinish = !this.modalFinish;
  }
  onShowModalSuccessFinish() {
    this.modalSuccessFinish = !this.modalSuccessFinish;
  }

  resetValueChat() {
    PusatResolusiV2Controller.setChatMessageFinish("");
    PusatResolusiV2Controller.setMentionSelectedFinish([]);
    PusatResolusiV2Controller.setUploadedFilesFinish([]);
  }

  async onSubmitFinish() {
    this.modalFinish = false;
    MainAppController.closeErrorMessage();
    MainAppController.showLoading();

    try {
      const finish = await PusatResolusiV2Controller.finishTicket({
        id: Number(this.detailId),
        // fill payload when work on ticket chat component
        payload: new FinishIssuePusatResolusiV2Request({
          message: PusatResolusiV2Controller.chatMessageFinish,
          mentions: JSON.stringify(PusatResolusiV2Controller.mentionSelectedFinish),
          attachments: PusatResolusiV2Controller.uploadedFilesFinish.filter((e): e is File => e !== null)
        })
      });
      if (finish) {
        this.onShowModalSuccessFinish();
        this.resetValueChat();
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          "Gagal meneyelesaikan kendala!",
          this.onSubmitFinish,
          this.resetValueChat
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  checkLoadedChat(conversation: Conversation[]) {
    if (conversation.length < this.historyChatData.limit) {
      this.isAllConversationLoaded = true;
    }
  }

  observeUnreadMessage() {
    const lastReadElement = this.getConversationElement(this.historyChatData.data.lastReadId);
    const observer = this.lastReadObserver

    if (lastReadElement) {
      observer.observe(lastReadElement);
      window.addEventListener("unmount-history-chat", () => {
        observer.disconnect();
      })
    }
  }

  lastReadObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.lastReadObserver.disconnect();
        this.historyChatData.data.unreadCount = 0;
        setTimeout(() => {
          this.isHighlight = false;
        }, 3000)
      }
    })
  })
}
